import React from "react";
import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericStepper, { type Step } from "@ui-components/Stepper/GenericStepper";
import { REQUEST_TYPES, type RequestTypeId } from "./requestTypes";

export interface RequestStep {
  id: string;
  title: string;
}

/** What the success screen shows once the ticket exists. */
export interface CreatedTicket {
  /** The Jira key ("KS-123"), when the endpoint sent one back. */
  key: string | null;
  /** Something that did not go through after the ticket was created. */
  warning: string | null;
}

interface RequestModalShellProps {
  type: RequestTypeId;
  isOpen: boolean;
  onClose: () => void;
  steps: RequestStep[];
  activeStepId: string;
  /** Whether the user may jump to a step from the stepper. */
  canActivate: (stepId: string) => boolean;
  onStepChange: (stepId: string) => void;
  /** Whether the active step is complete, which enables Next / Create. */
  canContinue: boolean;
  isSaving: boolean;
  onSubmit: () => void;
  /** Shown above the footer when creating the ticket failed or was refused. */
  submitError: string | null;
  /** Non-null once the ticket was created; the modal then shows the receipt. */
  created: CreatedTicket | null;
  children: React.ReactNode;
}

/**
 * The frame every "open a ticket" modal shares (CTX-1256): a header naming
 * the request type, the steps, the step's fields, and one footer with Back,
 * Next and Create. Each modal owns its fields and its payload; this owns the
 * navigation and the states around submitting, so the four read the same.
 */
export function RequestModalShell({
  type,
  isOpen,
  onClose,
  steps,
  activeStepId,
  canActivate,
  onStepChange,
  canContinue,
  isSaving,
  onSubmit,
  submitError,
  created,
  children,
}: RequestModalShellProps) {
  const activeIndex = Math.max(
    steps.findIndex((s) => s.id === activeStepId),
    0
  );
  const isLastStep = activeIndex === steps.length - 1;

  const stepperSteps: Step[] = steps.map((step, index) => ({
    title: step.title,
    status: stepStatus(index, activeIndex, canActivate(step.id)),
  }));

  const footer = created ? (
    <GenericButton label="Done" variant="brand" onClick={onClose} />
  ) : (
    <div className="flex w-full items-center justify-between gap-3">
      <GenericButton label="Cancel" variant="secondary" onClick={onClose} />
      <div className="flex gap-3">
        {activeIndex > 0 && (
          <GenericButton
            label="Back"
            variant="secondary"
            onClick={() => onStepChange(steps[activeIndex - 1].id)}
            disabled={isSaving}
          />
        )}
        {isLastStep ? (
          <GenericButton
            label={isSaving ? "Creating…" : "Create ticket"}
            variant="brand"
            onClick={onSubmit}
            loading={isSaving}
            disabled={!canContinue || isSaving}
          />
        ) : (
          <GenericButton
            label="Next"
            variant="brand"
            onClick={() => onStepChange(steps[activeIndex + 1].id)}
            disabled={!canContinue}
          />
        )}
      </div>
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      size="lg"
      fullScreenOnPhone
      title={<RequestModalTitle type={type} />}
      footer={<div className="w-full pb-4">{footer}</div>}
    >
      {created ? (
        <TicketCreated type={type} created={created} />
      ) : (
        <div className="space-y-5">
          {steps.length > 1 && (
            <GenericStepper
              steps={stepperSteps}
              size="sm"
              rowOnMobile
              onStepClick={(index) => onStepChange(steps[index].id)}
            />
          )}
          <div className="space-y-4">{children}</div>
          {submitError && <SubmitError message={submitError} />}
        </div>
      )}
    </GenericModal>
  );
}

function stepStatus(
  index: number,
  activeIndex: number,
  reachable: boolean
): Step["status"] {
  if (index === activeIndex) return "active";
  if (!reachable) return "disabled";
  return index < activeIndex ? "completed" : undefined;
}

function RequestModalTitle({ type }: { type: RequestTypeId }) {
  const { label, description, Icon } = REQUEST_TYPES[type];
  return (
    <span className="flex items-center gap-3">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-semantic-bg-brand-secondary text-semantic-text-brand">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-semantic-text-primary">{label}</span>
        <span className="block text-sm font-normal text-semantic-text-secondary">
          {description}
        </span>
      </span>
    </span>
  );
}

function SubmitError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary p-3 text-sm text-semantic-text-danger"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
      <span>{message}</span>
    </div>
  );
}

function TicketCreated({
  type,
  created,
}: {
  type: RequestTypeId;
  created: CreatedTicket;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-semantic-bg-success-secondary text-semantic-text-success">
        <CircleCheck className="h-6 w-6" aria-hidden />
      </span>
      <h3 className="text-base font-semibold text-semantic-text-primary">
        {REQUEST_TYPES[type].label} created
      </h3>
      <p className="max-w-sm text-sm text-semantic-text-secondary">
        {created.key ? (
          <>
            Your ticket is{" "}
            <span className="font-semibold text-semantic-text-primary">
              {created.key}
            </span>
            . You can follow it from the list on this page.
          </>
        ) : (
          "You can follow it from the list on this page."
        )}
      </p>
      {created.warning && (
        <div
          role="status"
          className="flex max-w-md items-start gap-2 rounded-md border border-semantic-border-warning-primary bg-semantic-bg-warning-secondary p-3 text-left text-sm text-semantic-text-warning"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
          <span>{created.warning}</span>
        </div>
      )}
    </div>
  );
}

export default RequestModalShell;
